import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import FormChangeDialog from './FormChangeDialog';
import { Button, ListItem, ListItemButton, useMediaQuery } from '@mui/material';
import ProfileIcon from '../../assets/img/icons/profile.svg';
import type { RootState} from 'store/reducers';
import { useStateSelector } from 'store/reducers';
import { useAppDispatch } from 'store';
import { asyncLogout } from '../../store/slices/authSlice';
import { useTranslation } from 'react-i18next';
import { editUsuario } from 'store/slices/userSlice';
import ModalConfigAnuncio from './modalConfigAnuncio';
import type { User } from 'views/Home/constants';

export type UserInfo = {
  nombre: string;
  email: string;
  rol: string;
  idioma: string;
};

const DialogProfile = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { usuario } = useStateSelector((state: RootState) => state.authState);
  const [userInfo, setUserInfo] = React.useState<UserInfo>({
    nombre: '',
    rol: '',
    idioma: '',
    email: '',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [openDialogChangePassword, setOpenDialogChangePassword] =
    useState(false);

  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const handleDialogCloseChangePassword = () => {
    setOpenDialogChangePassword(!openDialogChangePassword);
  };

  const handleOpenSettings = async () => {
    setOpenSettingsModal(true);
  };

  const handleOnIdiomaClick = (_e: React.MouseEvent) => {
    const newLocale = i18n.language === 'es' ? 'de' : 'es';
    localStorage.setItem('locale', newLocale);
    void i18n.changeLanguage(newLocale);
    void dispatch(
      editUsuario({
        data: { idioma: newLocale === 'es' ? 'es_ES' : 'de_De' },
        userId: usuario?.uid || '',
      }),
    );
  };

  const handleOpenChangePassword = () => {
    setOpenDialogChangePassword(true);
    setAnchorEl(null);
  };
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    void dispatch(asyncLogout());
  };

  useEffect(() => {
    const user = localStorage.getItem('user');
    const locale = localStorage.getItem('locale');
    setUserInfo({
      nombre: usuario?.displayName ?? '',
      idioma: locale || '',
      rol: user ? (JSON.parse(user) as User).rol : '',
      email: usuario?.email ?? '',
    });
  }, [usuario]);

  return (
    <>
      <div>
        {userInfo.rol === 'MONITOR' && (
          <Button onClick={handleOpenSettings}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              id="Layer_1"
              data-name="Layer 1"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path d="M12,0C5.383,0,0,5.383,0,12s5.383,12,12,12h12v-12C24,5.383,18.617,0,12,0Zm-5,13.5c-.828,0-1.5-.672-1.5-1.5s.672-1.5,1.5-1.5,1.5.672,1.5,1.5-.672,1.5-1.5,1.5Zm5,0c-.828,0-1.5-.672-1.5-1.5s.672-1.5,1.5-1.5,1.5.672,1.5,1.5-.672,1.5-1.5,1.5Zm5,0c-.828,0-1.5-.672-1.5-1.5s.672-1.5,1.5-1.5,1.5.672,1.5,1.5-.672,1.5-1.5,1.5Z" />
            </svg>
          </Button>
        )}

        <Button
          startIcon={<img src={ProfileIcon} alt="profile icon" />}
          color="inherit"
          aria-label="Profile"
          aria-controls="long-menu"
          aria-haspopup="true"
          onClick={handleOpenMenu}
        >
          <Typography variant="inherit" noWrap>
            {isDesktop ? userInfo.nombre : ''}
          </Typography>
        </Button>
      </div>

      {openSettingsModal && (
        <ModalConfigAnuncio
          openSettingsModal={openSettingsModal}
          handleClose={() => setOpenSettingsModal(false)}
        />
      )}

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={openMenu}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleCloseMenu}
      >
        <div data-tour="step-perfilbox">
          <ListItem
            sx={{ backgroundColor: '#f2e172 !important', cursor: 'auto' }}
          >
            <Box sx={{ pt: 1, pb: 1, pl: 2, pr: 2, display: 'flex' }}>
              <Box
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2">{userInfo.nombre}</Typography>
                <Typography variant="caption" noWrap gutterBottom>
                  {userInfo.email}
                </Typography>
                {[userInfo.rol].map((rolName, index) => (
                  <Typography key={index} variant="caption" noWrap>
                    <i>{rolName}</i>
                  </Typography>
                ))}
              </Box>
            </Box>
          </ListItem>
          <MenuItem onClick={handleOnIdiomaClick} data-tour="step-imagenperfil">
            <ListItemIcon>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography noWrap>
                  {t('idioma')}
                </Typography>
              }
            />
          </MenuItem>
          <MenuItem
            onClick={handleOpenChangePassword}
            data-tour="step-cambiarpass"
          >
            <ListItemIcon>
              <VpnKeyIcon />
            </ListItemIcon>
            <ListItemText primary="Cambiar contraseña" />
          </MenuItem>
          <MenuItem onClick={() => navigate('/help')} data-tour="step-ayuda">
            <ListItemIcon>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="Ayuda" />
          </MenuItem>
          {openDialogChangePassword && (
            <FormChangeDialog
              openDialog={openDialogChangePassword}
              handleDialogClose={handleDialogCloseChangePassword}
            />
          )}
          <ListItemButton
            onClick={() => handleLogout()}
            data-tour="step-cerrarsesion"
          >
            <ListItemIcon>
              <PowerSettingsNewIcon />
            </ListItemIcon>
            <ListItemText primary="Cerrar Sesión" />
          </ListItemButton>
        </div>
      </Menu>
    </>
  );
};

export default DialogProfile;
